
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DollarSign, Edit, Save, X } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getCurrentExchangeRate, createExchangeRate, updateExchangeRate } from "@/integrations/supabase/services";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

const ExchangeRateCard = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [newRate, setNewRate] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: exchangeRate, isLoading } = useQuery({
    queryKey: ['current-exchange-rate'],
    queryFn: getCurrentExchangeRate,
  });

  const updateMutation = useMutation({
    mutationFn: async (rate: number) => {
      const today = format(new Date(), 'yyyy-MM-dd');
      
      if (exchangeRate && format(new Date(exchangeRate.date), 'yyyy-MM-dd') === today) {
        return updateExchangeRate(exchangeRate.id, { usd_to_bs: rate });
      } else {
        return createExchangeRate({ 
          usd_to_bs: rate, 
          date: today 
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['current-exchange-rate'] });
      setIsEditing(false);
      setNewRate("");
      toast({
        title: "Tasa actualizada",
        description: "La tasa de cambio ha sido actualizada correctamente.",
      });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Error al actualizar la tasa de cambio.",
      });
    },
  });

  const handleSave = () => {
    const rate = parseFloat(newRate);
    if (rate > 0) {
      updateMutation.mutate(rate);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setNewRate(exchangeRate?.usd_to_bs.toString() || "");
  };

  const handleCancel = () => {
    setIsEditing(false);
    setNewRate("");
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">Cargando...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">Tasa USD/Bs</CardTitle>
        <DollarSign className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <div className="space-y-4">
            <Input
              type="number"
              step="0.01"
              value={newRate}
              onChange={(e) => setNewRate(e.target.value)}
              placeholder="Ingrese nueva tasa"
              className="text-xl font-bold"
            />
            <div className="flex gap-2">
              <Button 
                size="sm" 
                onClick={handleSave}
                disabled={!newRate || updateMutation.isPending}
              >
                <Save className="h-4 w-4 mr-1" />
              </Button>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={handleCancel}
                disabled={updateMutation.isPending}
              >
                <X className="h-4 w-4 mr-1" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            <div className="text-2xl font-bold">
              {exchangeRate?.usd_to_bs.toFixed(2) || "ND"} Bs &nbsp;
              <Button 
                size="sm" 
                variant="outline" 
                onClick={handleEdit}
              >
                <Edit className="h-4 w-4 mr-1" />
            </Button>
            </div>
            <div className="text-xs text-muted-foreground">
              Actualizado: {exchangeRate ? format(new Date(exchangeRate.date), 'dd/MM/yyyy') : 'Hoy'}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ExchangeRateCard;
